import type { PickerProps } from 'antd/es/date-picker/generatePicker';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import useT from '@tools/ui-hooks/useT';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import {
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

const AntDatePicker = generatePicker<Moment>(momentGenerateConfig);

type AntDatePickerFieldProps = Pick<
  PickerProps<Moment>,
  | 'value'
  | 'defaultValue'
  | 'placeholder'
  | 'disabled'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'className'
  | 'id'
  | 'name'
  | 'aria-label'
  | 'aria-labelledby'
>;

export type DateInputProps = AntDatePickerFieldProps &
  Partial<{
    readOnly: boolean;
    status: 'error' | 'default';
    format: string;
    label: string;
    optional: boolean;
    infoIcon: boolean;
    infoText: string;
    helpText: string;
  }>;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntDatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  any
>((props, ref) => <AntDatePicker {...props} ref={ref} />);

const StyledDatePicker = styled(ForwardedAntDatePicker)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
}>`
  &&.ant-picker {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0 ${theme.dime.spacing.l};
    height: ${theme.dime.spacing['3xl']};
    box-shadow: none;
    width: 100%;
    margin-bottom: ${theme.dime.spacing.m};
  }

  &&.ant-picker-disabled {
    cursor: not-allowed;
  }

  &&.ant-picker-disabled input::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-picker input {
    cursor: ${({ $disabled, $readOnly }) =>
      $disabled || $readOnly ? 'not-allowed' : 'text'};
    background-color: transparent;
  }
`;

export const DateInput = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  DateInputProps
>(
  (
    {
      value,
      defaultValue,
      placeholder,
      disabled = false,
      readOnly = false,
      onChange,
      onFocus,
      onBlur,
      className = '',
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      status = 'default',
      format = 'MM/DD/YYYY',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      ...rest
    },
    ref
  ) => {
    const { t } = useT();
    const hasError = status === 'error';
    const resolvedPlaceholder = placeholder ?? t('dateInput.placeholder');
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;

    return (
      <InputWrapper className={className}>
        {!!label && (
          <InputLabel
            id={labelId}
            label={label}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledDatePicker
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          inputReadOnly={readOnly}
          open={readOnly ? false : undefined}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          inputMode="numeric"
          id={inputId}
          name={name}
          aria-label={ariaLabel}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          format={format}
          allowClear={
            disabled || readOnly
              ? false
              : {
                  clearIcon: (
                    <IconWrap
                      type={IconType.Close}
                      size={IconSize.Small}
                      style={{ color: theme.dime.colors.icon.primary }}
                    />
                  )
                }
          }
          suffixIcon={
            <IconWrap
              type={IconType.Calendar}
              size={IconSize.Small}
              disabled={disabled || readOnly}
              style={
                disabled || readOnly
                  ? undefined
                  : { color: theme.dime.colors.icon.primary }
              }
            />
          }
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
      </InputWrapper>
    );
  }
);

DateInput.displayName = 'DateInput';
